/**
 * ============================================================================
 * [P5-02] TrashBinModal - 휴지통 모달 컴포넌트
 *
 * 역할:
 * - 소프트 삭제된 글 목록 표시
 * - 복원 및 영구 삭제 기능
 *
 * Safety:
 * - createPortal 사용
 * - ESC 키로 닫기
 * ============================================================================
 */

import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { SavedText } from '@/types'
import styles from './Saved.module.css'

// ============================================================================
// Props 타입 정의
// ============================================================================
interface TrashBinModalProps {
  isOpen: boolean
  onClose: () => void
  deletedTexts: SavedText[]
  onRestore: (id: string) => Promise<boolean>
  onPermanentDelete: (id: string) => Promise<boolean>
}

// ============================================================================
// Component
// ============================================================================
export function TrashBinModal({
  isOpen,
  onClose,
  deletedTexts,
  onRestore,
  onPermanentDelete,
}: TrashBinModalProps) {
  // ========================================================================
  // ESC 키로 닫기
  // ========================================================================
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handleEsc)
    }
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  // 포털 컨테이너 확인
  if (typeof document === 'undefined') return null
  if (!isOpen) return null

  // ========================================================================
  // 날짜 포맷
  // ========================================================================
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '날짜 없음'
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleString('ko-KR')
  }

  // ========================================================================
  // 핸들러
  // ========================================================================
  const handleRestore = async (id: string) => {
    const success = await onRestore(id)
    if (success) {
      // 복원 성공 시 알림 (선택적)
    }
  }

  const handlePermanentDelete = async (id: string) => {
    if (!confirm('정말 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return
    }
    await onPermanentDelete(id)
  }

  // ========================================================================
  // Render
  // ========================================================================
  return createPortal(
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className={styles.modalHeader}>
          <h2>🗑️ 휴지통</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="닫기">
            &times;
          </button>
        </div>

        {/* 내용 */}
        <div className={styles.trashBinContent}>
          {deletedTexts.length === 0 ? (
            <div className={styles.emptyState}>
              <p>🗑️</p>
              <p>휴지통이 비었습니다.</p>
            </div>
          ) : (
            <div className={styles.trashBinList}>
              {deletedTexts.map((item) => (
                <div key={item.id} className={styles.trashItem}>
                  <div className={styles.trashItemHeader}>
                    <span className={styles.trashItemType}>
                      {item.type === 'reference' ? '📖 레퍼런스' : '✏️ 작성글'}
                    </span>
                    <span className={styles.trashItemDate}>
                      삭제일: {formatDate(item.deletedAt)}
                    </span>
                  </div>
                  <div className={styles.trashItemContent}>
                    {item.content.length > 100
                      ? item.content.substring(0, 100) + '...'
                      : item.content}
                  </div>
                  <div className={styles.trashItemActions}>
                    <button
                      className={styles.restoreBtn}
                      onClick={() => handleRestore(item.id)}
                      aria-label="글 복원"
                    >
                      ♻️ 복원
                    </button>
                    <button
                      className={styles.permanentDeleteBtn}
                      onClick={() => handlePermanentDelete(item.id)}
                      aria-label="영구 삭제"
                    >
                      🔥 영구 삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default TrashBinModal
